import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import BDPLayout from "../../components/bdp/BDPLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { DollarSign, Trophy, Target, Loader2 } from "lucide-react";

const BDPLeaderboard = () => {
  const { API, token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/bdp/leaderboard`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) setData(await r.json());
      } catch { // error handled silently
        console.error("Request failed:", error);
    }
      setLoading(false);
    })();
  }, [API, token]);

  if (loading) return <BDPLayout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div></BDPLayout>;

  const rankColors = ["text-yellow-500", "text-gray-400", "text-amber-700"];
  const rankBg = ["bg-yellow-50 border-yellow-200", "bg-gray-50 border-gray-200", "bg-amber-50 border-amber-200"];

  const LeaderboardTable = ({ title, icon: Icon, entries, valueKey, valueFormatter, subtitle }) => (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
            <Icon className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="font-bold text-[#1A2636]">{title}</h2>
            <p className="text-xs text-gray-500">{subtitle}</p>
          </div>
        </div>
        {entries?.length ? (
          <div className="space-y-2">
            {entries.map((entry, i) => {
              const isMe = entry.agent_code === user?.agent_code;
              return (
                <div
                  key={entry.agent_code}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    i < 3 ? rankBg[i] : "bg-white border-gray-100"
                  } ${isMe ? "ring-2 ring-emerald-400 ring-offset-1" : ""}`}
                  data-testid={`rank-${i}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`text-lg font-bold w-8 text-center ${i < 3 ? rankColors[i] : "text-gray-400"}`}>
                      {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}
                    </span>
                    <div>
                      <span className="text-sm font-semibold text-[#1A2636]">
                        {entry.name}
                        {isMe && <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-[10px]">You</Badge>}
                      </span>
                      <p className="text-[11px] text-gray-400 font-mono">{entry.agent_code}</p>
                    </div>
                  </div>
                  <p className="text-base font-bold text-[#1A2636]">{valueFormatter(entry[valueKey])}</p>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-6">No data yet</p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <BDPLayout>
      <div className="space-y-6" data-testid="bdp-leaderboard">
        <div>
          <h1 className="text-2xl font-bold text-[#1A2636]">Leaderboards</h1>
          <p className="text-sm text-gray-500 mt-1">See how you stack up against the team</p>
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <LeaderboardTable
            title="Total Sales Volume"
            icon={DollarSign}
            entries={data?.by_sales}
            valueKey="total_sales"
            valueFormatter={(v) => `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2 })}`}
            subtitle="Ranked by total revenue generated"
          />
          <LeaderboardTable
            title="Closed Deals"
            icon={Target}
            entries={data?.by_deals}
            valueKey="deals_closed"
            valueFormatter={(v) => `${v} deals`}
            subtitle="Ranked by number of completed deals"
          />
        </div>
      </div>
    </BDPLayout>
  );
};

export default BDPLeaderboard;
